export const capitalizeFirstLetter = (val: string) =>
    String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase()

/**
 * Format a date string from ISO format to DD/MM/YYYY
 * Only formats ISO dates (containing "T" or in YYYY-MM-DD format)
 * Returns the original string if it's not in ISO format (e.g., partial user input)
 *
 * @param dateStr - Date string in ISO format (e.g., "1993-10-27T14:49:00+00:00") or other format
 * @returns Formatted date string in DD/MM/YYYY format, or original string if not ISO
 */
export const formatDateToDDMMYYYY = (dateStr: string | null | undefined): string => {
    if (!dateStr) return ''

    // If already in DD/MM/YYYY format (complete or partial), return as-is
    // This preserves user input while editing
    if (dateStr.includes('/')) {
        return dateStr
    }

    // Only format if it looks like an ISO date (contains "T" or matches YYYY-MM-DD)
    const isISOFormat = dateStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(dateStr)
    if (!isISOFormat) {
        return dateStr // Return as-is if not ISO format
    }

    // Try to parse ISO format
    try {
        const date = new Date(dateStr)
        if (!isNaN(date.getTime())) {
            const day = date.getDate().toString().padStart(2, '0')
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear()
            return `${day}/${month}/${year}`
        }
    } catch {
        // If parsing fails, return original string
        return dateStr
    }

    return dateStr
}

/**
 * Parse a date string in DD/MM/YYYY or ISO format into a Date.
 * The loan API serializes dates in ISO while the forms sync them in DD/MM/YYYY,
 * so both formats can coexist in the store depending on the navigation path.
 *
 * @returns The parsed Date, or null if the string matches neither format
 */
export const parseDDMMYYYYOrISODate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null

    const parts = dateStr.split('/')
    if (parts.length === 3) {
        const [day, month, year] = parts.map(Number)
        if (!day || !month || !year) return null
        return new Date(year, month - 1, day)
    }

    const isISOFormat = dateStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(dateStr)
    if (!isISOFormat) return null

    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? null : date
}

/**
 * Mask raw user input as a DD/MM/YYYY date by stripping non-digits and
 * inserting "/" between day/month and month/year as soon as the next part
 * starts being typed (no trailing slash on partial input).
 */
export const formatDateInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8)

    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.substring(0, 2)}/${digits.substring(2)}`
    return `${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4)}`
}

// Accent-insensitive search: NFD splits an accented letter into base + combining mark, dropped here.
export const foldForSearch = (value: string): string =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
